<script setup lang="tsx">
  import CustomDropdown from '~/components/UI/Forms/CustomDropdown.vue';
  import CustomInput from '~/components/UI/Forms/CustomInput.vue';
  import CustomTextArea from '~/components/UI/Forms/CustomTextArea.vue';
  import RoleBasedButton from '~/components/UI/Others/RoleBasedButton.vue';
  import { get, post } from '~/services/donationScreen';
  import type { GetResponse, PurposeDrawer, ResponseStatus, PostResponse, OrganizationResponse } from '~/types';
  import EditPasswordModal from '../../Modals/EditPasswordModal.vue';
  import AuditLogs from '../RoutedDrawerComponents/AuditLogs.vue';

  const props = defineProps<{
    onClose: () => void;
    identifier: string;
  }>();

  const route = useRoute();

  const action = computed(() => {
    return route.query.action as string;
  });

  const formRef = ref();
  const formState = reactive({
    purposeName: '',
    purposeCategory: '',
    purposeDimension: '',
    purposeDescription: '',
    auditLogData: []
  });
  const status = ref<boolean>(false);
  const purposeDimension = ref<string | string[]>('');
  const purposeDimesntionsOptions = ref<{ title: string; value: string }[]>([]);
  const isModalVisible = ref<boolean>(false);
  const isModalVisibleForDisable = ref<boolean>(false);
  const editStatus = ref<ResponseStatus>('idle');

  const fetchData = async () => {
    try {
      const dimensionsResponse = await get<GetResponse<string[]>>(`v1/get_purpose_dimensions`);
      purposeDimesntionsOptions.value = dimensionsResponse.data.data.map(dimension => ({
        title: dimension,
        value: dimension
      }));

      if (props.identifier) {
        const purposeResponse = await get<GetResponse<PurposeDrawer>>(`v1/get_purpose/${props.identifier}`);
        status.value = purposeResponse?.data?.data?.selected_purpose?.is_disabled;
        if (purposeResponse.data) {
          Object.assign(formState, {
            purposeName: purposeResponse?.data?.data?.selected_purpose?.purpose_name || '',
            purposeCategory: purposeResponse?.data?.data?.selected_purpose?.purpose_category || '',
            purposeDimension: purposeResponse?.data?.data?.selected_purpose?.purpose_dimension || '',
            purposeDescription: purposeResponse?.data?.data?.selected_purpose?.purpose_description || '',
            auditLogData: purposeResponse?.data?.data?.audit_log_data || []
          });
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const rules = {
    purposeName: [
      {
        required: true,
        message: 'Purpose Name is required',
        trigger: 'submit'
      }
    ],
    purposeCategory: [
      {
        required: true,
        message: 'Purpose Category is required',
        trigger: 'submit'
      }
    ],
    purposeDimension: [
      {
        required: purposeDimension.value === 'Yes' ? true : false,
        message: 'Purpose Dimension is required',
        trigger: 'submit'
      }
    ],
    purposeDescription: [
      {
        required: true,
        message: 'Purpose Description is required',
        trigger: 'submit'
      }
    ]
  };

  const updateStateField = (value: string, stateKey: keyof typeof formState) => {
    (formState as any)[stateKey] = value;
  };

  const submitHandler = async (modalData: { password: string }) => {
    try {
      editStatus.value = 'pending';
      const response = await post<PostResponse<PurposeDrawer>>(`v1/purpose/${props.identifier}`, {
        data: {
          purpose_name: formState.purposeName,
          purpose_category: formState.purposeCategory,
          purpose_dimension: formState.purposeDimension,
          purpose_description: formState.purposeDescription,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification(response?.data?.message);
        isModalVisible.value = false;
        props.onClose();
      } else if (response?.data?.status === 'error') {
        openFailedCustomNotification(response?.data?.message);
        isModalVisible.value = false;
        props.onClose();
      } else {
        openFailedCustomNotification('Failed to Update Purpose');
        editStatus.value = 'error';
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Update Purpose');
      editStatus.value = 'error';
    }
  };

  const handleCreate = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<PostResponse<PurposeDrawer>>('v1/add_new_purpose', {
        data: {
          purpose_name: formState.purposeName,
          purpose_category: formState.purposeCategory,
          purpose_dimension: formState.purposeDimension,
          purpose_description: formState.purposeDescription
        }
      });

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Purpose Created Successfully');
        props.onClose();
        editStatus.value = 'success';
      } else if (response.data.status === 'error') {
        editStatus.value = 'error';
        return openFailedCustomNotification(response.data.message || 'Failed to Create Purpose');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Create Purpose');
      editStatus.value = 'error';
    }
  };

  const handleCreatePurpose = async () => {
    try {
      await formRef.value.validate();

      if (route.query.action === 'edit') {
        return openModal();
      }

      await handleCreate();
    } catch (error) {
      console.error(error);
    }
  };

  const openDisableModal = async () => {
    try {
      isModalVisibleForDisable.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const openModal = async () => {
    try {
      const isFormValid = await formRef.value.validate();
      if (!isFormValid) throw new Error('Form validation failed!');

      isModalVisible.value = true;
    } catch (error) {
      console.error(error);
    }
  };

  const handleDisablePurpose = async (modalData: { password: string }) => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/purpose_disabled/${props.identifier}`, {
        data: {
          purpose_name: formState.purposeName,
          purpose_category: formState.purposeCategory,
          purpose_dimension: formState.purposeDimension,
          purpose_description: formState.purposeDescription,
          is_disabled: true,
          password: modalData.password
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Purpose Disabled Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Disable Purpose');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Disable Purpose');
      editStatus.value = 'error';
    }
  };

  const handleActivePurpose = async () => {
    editStatus.value = 'pending';
    try {
      const response = await post<any>(`/v1/purpose_enabled/${props.identifier}`, {
        data: {
          is_disabled: false
        }
      });

      if (response.status === 401) {
        editStatus.value = 'error';
        return openFailedCustomNotification('Wrong Password!');
      }

      if (response.data.status === 'success') {
        openSuccessfulCustomNotification('Purpose Activated Successfully');
        isModalVisibleForDisable.value = false;
        props.onClose();
        editStatus.value = 'success';
      } else {
        editStatus.value = 'error';
        openFailedCustomNotification(response.data.message || 'Failed to Activate Purpose');
      }
    } catch (error) {
      console.error(error);
      openFailedCustomNotification('Failed to Activate Purpose');
      editStatus.value = 'error';
    }
  };

  const handleModalCancel = () => {
    isModalVisible.value = false;
  };

  const handleModalDisableCancel = () => {
    isModalVisibleForDisable.value = false;
  };

  onMounted(async () => {
    const response = await get<OrganizationResponse>('/v1/get_org_info');
    const dimensions = response.data.data.purpose_dimensions;
    purposeDimension.value = purposeDimension.value = Array.isArray(dimensions) ? dimensions[0] : dimensions || '';
    fetchData();
  });
</script>

<template>
  <a-drawer
    :open="route.query.drawerFrom === 'purposes'"
    :title="
      action === 'edit' ? 'Update Purpose' : action === 'add' || action === 'duplicate' ? 'Add New Purpose' : 'Purpose'
    "
    width="750"
    class="monetary-drawer"
    :body-style="{ padding: '0px' }"
    :header-style="{
      padding: '12px',
      borderBottom: '1px solid #E4E4E7'
    }"
    @close="props.onClose"
  >
    <template #extra>
      <div class="flex gap-3">
        <RoleBasedButton
          v-if="!props.identifier || action"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded sm-medium duration-150 border border-blue-600"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleCreatePurpose"
        >
          {{ action === 'edit' ? 'Update Purpose' : 'Create Purpose' }}
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && !status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-red-600 hover:bg-red-700 text-white rounded sm-medium duration-150 border border-red-600 hover:!text-white hover:!border-red-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="openDisableModal"
        >
          {{ 'Disable Purpose' }}
        </RoleBasedButton>
        <RoleBasedButton
          v-if="action === 'edit' && status"
          component-name="addActionButton"
          active-class="py-2 px-3 bg-green-600 hover:bg-green-700 text-white rounded sm-medium duration-150 border border-green-600 hover:!text-white hover:!border-green-700"
          disabled-class="py-2 px-3 rounded sm-medium duration-150 !bg-gray-200 !shadow-[0px_1px_3px_0px_#0000001A,0px_1px_2px_0px_#0000000F] border-none"
          :loading="editStatus === 'pending'"
          @click="handleActivePurpose"
        >
          {{ 'Active Purpose' }}
        </RoleBasedButton>
      </div>
    </template>
    <a-form
      ref="formRef"
      class="flex flex-col gap-4 p-3 setup-drawer-form"
      :model="formState"
      :rules="rules"
      :colon="false"
      :label-col="{ style: { width: '250px', textAlign: 'left' } }"
      hide-required-mark="true"
    >
      <CustomInput
        label-text="Purpose Name"
        name="purposeName"
        :value="formState.purposeName"
        state-key="purposeName"
        :is-compulsory="true"
        :is-disabled="!action"
        @update:value="updateStateField"
      />
      <CustomDropdown
        :options="[
          { title: 'Unrestricted', value: 'Unrestricted' },
          { title: 'Restricted', value: 'Restricted' },
          { title: 'Corpus', value: 'Corpus' },
          { title: 'Other Receipts', value: 'Other Receipts' }
        ]"
        label-text="Purpose Category"
        :label-width="'250px'"
        name="purposeCategory"
        :value="formState.purposeCategory"
        state-key="purposeCategory"
        :rule="rules.purposeCategory"
        :is-compulsory="true"
        :disabled="!action"
        @update:value="updateStateField"
      />
      <CustomDropdown
        v-if="purposeDimension === 'Yes'"
        :options="purposeDimesntionsOptions"
        label-text="Purpose Dimension"
        :label-width="'250px'"
        name="purposeDimension"
        :value="formState.purposeDimension"
        state-key="purposeDimension"
        :rule="rules.purposeDimension"
        :is-compulsory="true"
        :disabled="!action"
        @update:value="updateStateField"
      />
      <CustomTextArea
        label="Purpose Description"
        name="purposeDescription"
        :value="formState.purposeDescription"
        state-key="purposeDescription"
        :is-disabled="!action"
        :is-compulsory="true"
        @update:value="updateStateField"
      />
    </a-form>
    <AuditLogs :items="formState.auditLogData" title="Purpose" class="mx-3" />
    <EditPasswordModal
      :visible="isModalVisible"
      :loading="editStatus === 'pending'"
      modal-title="Update Purpose"
      @update:cancel="handleModalCancel"
      @update:update="submitHandler"
    />
    <EditPasswordModal
      :visible="isModalVisibleForDisable"
      :loading="editStatus === 'pending'"
      modal-title="Disable Purpose"
      :warning-message="'Are you sure you want to Disable this Purpose?'"
      @update:cancel="handleModalDisableCancel"
      @update:update="handleDisablePurpose"
    />
  </a-drawer>
</template>

<style>
  @import '~/assets/css/donationDrawer.css';

  .setup-drawer-form .ant-form-item {
    margin-bottom: 0px;
  }
</style>
